/**
 * RefactorCodeView — Refactor Code Flow (wired to backend)
 *
 * 4-step hybrid flow:
 *   1. Select Scope       — wizard card: choose svcgen job + services to analyze
 *   2. Configure          — wizard card: toggle refactor pattern types
 *   3. Review             — FULL workspace (3-panel): file tree / recommendations / grounding
 *   4. Review Plan        — wizard card: summary table + Finalize CTA
 *
 * Phase 4: All backend calls wired. No mock data. No mock polling.
 * Reuses CSS variable patterns and header/step-sidebar from GenerateDocsView.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowLeft, ChevronRight, ChevronDown, Settings, HelpCircle, User,
  Home, Workflow, Plug, FileBarChart, BarChart2, LayoutGrid,
  CheckCircle2, Circle, Lock,
  FolderOpen, Folder, FileCode, ChevronUp,
  ToggleLeft, ToggleRight,
  Code2, Tag, Cpu, AlignLeft, BookOpen,
  AlertCircle, Play, Loader2, RefreshCw, Package, XCircle, Download,
} from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './RefactorCodeView.css'
import '../runs/RunsView.css'
import '../docs/GenerateDocsView.css'

// ─── Pattern config options ───────────────────────────────────────────────────

const PATTERNS = [
  {
    id: 'OOP',
    label: 'OOP Improvements',
    desc: 'Extract classes, reduce coupling, apply single-responsibility principle.',
    icon: Code2,
    defaultOn: true,
    advanced: false,
  },
  {
    id: 'MVC',
    label: 'MVC Alignment',
    desc: 'Separate concerns into controller, service, and repository layers.',
    icon: AlignLeft,
    defaultOn: false,
    advanced: true,
  },
  {
    id: 'Naming',
    label: 'Naming Cleanup',
    desc: 'Convert COBOL-style names (WS_CUST_ID) to Java camelCase conventions.',
    icon: Tag,
    defaultOn: true,
    advanced: false,
  },
  {
    id: 'Structure',
    label: 'Structure Cleanup',
    desc: 'Shorten long methods, remove dead code, simplify conditionals.',
    icon: Cpu,
    defaultOn: true,
    advanced: false,
  },
  {
    id: 'Maintainability',
    label: 'Maintainability Patterns',
    desc: 'Reduce cyclomatic complexity and improve overall readability.',
    icon: BookOpen,
    defaultOn: true,
    advanced: false,
  },
]

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Select Scope',           desc: 'Choose services to analyze'        },
  { id: 2, label: 'Configure',              desc: 'Select refactor pattern types'     },
  { id: 3, label: 'Review Recommendations', desc: 'Confirm or skip each suggestion'   },
  { id: 4, label: 'Review Plan',            desc: 'Summary and finalize'              },
  { id: 5, label: 'Apply Refactor Plan',     desc: 'Apply patches and create code checkpoint' },
]

// ─── TYPE badge colours ───────────────────────────────────────────────────────

const TYPE_COLOR = {
  OOP:                 'blue',
  MVC:                 'purple',
  Naming:              'orange',
  Structure:           'teal',
  Maintainability:     'pink',
  LCOM4_SPLIT:         'blue',
  LARGE_CLASS:         'blue',
  LONG_METHOD:         'teal',
  DEAD_METHOD:         'pink',
  COBOL_IDENTIFIER:    'orange',
  COMMENTED_CODE:      'orange',
  EMPTY_CATCH:         'pink',
  PRIMITIVE_OBSESSION: 'purple',
  FEATURE_ENVY:        'teal',
  DUPLICATE_CODE:      'pink',
  MVC_MIXED:           'purple',
  CIRCULAR_DEPENDENCY: 'orange',
  EVALUATE_STRATEGY:   'teal',
}

// ─── Auth helper ──────────────────────────────────────────────────────────────

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('modernizeit_token')
  const headers = { ...extra }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} }
}

// ─── Component ────────────────────────────────────────────────────────────────

function RefactorCodeView({
  project,
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToExtractBizLogic,
  onGoToDecomposeCode,
  onGoToServiceGeneration,
  onGoToJavaPkg,
  onGoToRefineCode,
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
}) {
  const apiBase     = getAPIBaseUrl()
  const accountId   = project?.account || ''
  const projectName = project?.name || 'Project'

  // ── Wizard navigation
  const [activeStep, setActiveStep] = useState(1)

  // ── Step 1: Scope — svcgen job picker + service selector
  const [svcgenJobs,      setSvcgenJobs]      = useState([])        // [{job_id, application_name, service_count, completed_at}]
  const [svcgenLoading,   setSvcgenLoading]   = useState(false)
  const [svcgenError,     setSvcgenError]     = useState(null)
  const [selectedJobId,   setSelectedJobId]   = useState(null)      // chosen svcgen job_id
  const [showAllJobs,     setShowAllJobs]     = useState(false)     // collapse older runs
  const [scopeMode,       setScopeMode]       = useState('all')     // 'all' | 'selected'
  const [selectedServices,setSelectedServices]= useState(new Set())
  const [serviceList,     setServiceList]     = useState([])        // service names from svcgen job

  // ── Step 2: Config
  const [enabledPatterns, setEnabledPatterns] = useState(() => {
    const m = {}
    PATTERNS.forEach(p => { m[p.id] = p.defaultOn })
    return m
  })

  // ── Analysis job
  const [jobId,         setJobId]         = useState(null)
  const [jobStatus,     setJobStatus]     = useState('idle')   // 'idle'|'starting'|'running'|'completed'|'failed'
  const [jobProgress,   setJobProgress]   = useState(0)
  const [jobPhase,      setJobPhase]      = useState('')
  const [jobError,      setJobError]      = useState(null)
  const pollRef = useRef(null)

  // ── Step 3: Review workspace — recommendations from backend
  const [recommendations, setRecommendations] = useState([]) // flat list
  const [recsByFile,      setRecsByFile]      = useState({}) // {filename: [rec, ...]}
  const [filesByService,  setFilesByService]  = useState({}) // {svcName: [filename, ...]}
  const [cobolRefByFile,  setCobolRefByFile]  = useState({}) // {filename: cobol_ref_str}
  const [recsLoading,     setRecsLoading]     = useState(false)
  const [recsError,       setRecsError]       = useState(null)

  const [expandedServices, setExpandedServices] = useState(new Set())
  const [selectedFile,     setSelectedFile]     = useState(null)
  const [recState,         setRecState]         = useState({}) // {recId: 'confirmed'|'skipped'}
  const [decisionErrors,   setDecisionErrors]   = useState({}) // {recId: errorMsg}

  // ── Step 4: Finalize
  const [finalizing,       setFinalizing]       = useState(false)
  const [finalizeError,    setFinalizeError]    = useState(null)
  const [finalized,        setFinalized]        = useState(false)
  const [finalizeResult,   setFinalizeResult]   = useState(null) // {confirmed_count, skipped_count}

  // ── Step 5: Refine Code
  const [rfneJobId,          setRfneJobId]          = useState(null)
  const [rfneStatus,         setRfneStatus]         = useState('idle')  // 'idle'|'starting'|'polling'|'succeeded'|'failed'|'compile_error'
  const [rfnePackageUri,     setRfnePackageUri]     = useState(null)    // set when executor uploads ZIP
  const [rfneDownloading,    setRfneDownloading]    = useState(false)
  const [rfneError,          setRfneError]          = useState(null)
  const [rfnePatchCounts,    setRfnePatchCounts]    = useState(null)    // {applied, failed, compile_passed, compile_errors}
  const [rfnePatchReport,    setRfnePatchReport]    = useState(null)    // full patch_report.json
  const [rfneCompileReport,  setRfneCompileReport]  = useState(null)    // compile_report.json (on failure)
  const [rfneReportLoading,  setRfneReportLoading]  = useState(false)

  // Patch Results table — sort + filter state
  const [patchSortCol,      setPatchSortCol]      = useState('file')   // 'file'|'status'|'notes'
  const [patchSortDir,      setPatchSortDir]      = useState('asc')    // 'asc'|'desc'
  const [patchStatusFilter, setPatchStatusFilter] = useState('')        // ''=all | normalized status key
  const rfnePollRef = useRef(null)

  // ── Misc UI
  const [showUserMenu,  setShowUserMenu]  = useState(false)
  const [showMoreMenu,  setShowMoreMenu]  = useState(false)
  const [expandedSkipped, setExpandedSkipped] = useState(false)

  // ── Bulk actions
  const [bulkPattern,    setBulkPattern]    = useState('')       // pattern type filter for pattern-scoped bulk
  const [bulkDialog,     setBulkDialog]     = useState(null)     // null | {decision, scope, targets, countsByType}
  const [bulkInProgress, setBulkInProgress] = useState(false)   // true while sequential POSTs are running
  const [bulkResult,     setBulkResult]     = useState(null)    // null | {succeeded, failed, firstFailId, firstFailMsg}

  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  // ── Derived counts ────────────────────────────────────────────────────────

  const confirmedIds = Object.entries(recState).filter(([, v]) => v === 'confirmed').map(([k]) => k)
  const skippedIds   = Object.entries(recState).filter(([, v]) => v === 'skipped').map(([k]) => k)

  const allRecs        = recommendations
  const confirmedCount = confirmedIds.length
  const skippedCount   = skippedIds.length
  const pendingCount   = allRecs.length - confirmedCount - skippedCount
  const confirmedRecs  = allRecs.filter(r => confirmedIds.includes(r.id))

  const fileRecs = selectedFile ? (recsByFile[selectedFile] || []) : []

  // Active services for the file tree
  const activeServices = scopeMode === 'all'
    ? Object.keys(filesByService)
    : Object.keys(filesByService).filter(s => selectedServices.has(s))

  // ── Load svcgen jobs on mount ─────────────────────────────────────────────

  useEffect(() => {
    if (!accountId) return
    loadSvcgenJobs()
  }, [accountId])

  const loadSvcgenJobs = useCallback(async () => {
    setSvcgenLoading(true)
    setSvcgenError(null)
    try {
      const storedUser = getStoredUser()
      const userId = storedUser.user_id || storedUser.id || ''
      // Pass application_name so the backend only returns jobs for this project
      const url = `${apiBase}/refactor-code/available-svcgen-jobs` +
        `?account_id=${encodeURIComponent(accountId)}` +
        `&user_id=${encodeURIComponent(userId)}` +
        (projectName ? `&application_name=${encodeURIComponent(projectName)}` : '')
      const resp = await fetch(url, { headers: getAuthHeaders() })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }
      const jobs = await resp.json()
      const mapped = jobs.map(j => {
        // If label starts with " — " the backend had a blank app_name — patch it here
        let lbl = j.label || j.application_name || j.job_id
        if (lbl.startsWith(' — ') && projectName) {
          lbl = projectName + lbl
        }
        return {
          job_id:           j.job_id,
          application_name: j.application_name || projectName,
          service_count:    null,
          completed_at:     j.completed_at || j.created_at || '',
          label:            lbl,
        }
      })

      setSvcgenJobs(mapped)
      // Auto-select the newest job (first in list — backend sorts newest-first)
      if (mapped.length > 0 && !selectedJobId) {
        setSelectedJobId(mapped[0].job_id)
      }
    } catch (e) {
      setSvcgenError(e.message)
    } finally {
      setSvcgenLoading(false)
    }
  }, [apiBase, accountId, projectName, selectedJobId])

  // ── Load service list when svcgen job changes ─────────────────────────────

  useEffect(() => {
    if (!selectedJobId) return
    loadServiceList(selectedJobId)
  }, [selectedJobId])

  const loadServiceList = useCallback(async (jid) => {
    try {
      const resp = await fetch(`${apiBase}/jobs/${encodeURIComponent(jid)}`, {
        headers: getAuthHeaders(),
      })
      if (!resp.ok) return
      const data = await resp.json()
      const od = (() => {
        try { return typeof data.output_data === 'string' ? JSON.parse(data.output_data) : (data.output_data || {}) } catch { return {} }
      })()
      // Extract service names from output_data
      const svcs = od.services?.map(s => s.service_name || s.name || s) ||
                   od.service_names ||
                   []
      setServiceList(svcs)
      setSelectedServices(new Set(svcs))
    } catch (_) {
      // Non-fatal — service list stays empty, user can still run
    }
  }, [apiBase])

  // ── Cleanup poll on unmount ───────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (pollRef.current)     clearInterval(pollRef.current)
      if (rfnePollRef.current) clearInterval(rfnePollRef.current)
    }
  }, [])

  // ── Poll effect: fires when jobId is set and status is running ────────────

  useEffect(() => {
    if (!jobId || jobStatus !== 'running') return

    const poll = async () => {
      try {
        const resp = await fetch(
          `${apiBase}/refactor-code/status/${encodeURIComponent(jobId)}`,
          { headers: getAuthHeaders() }
        )
        if (!resp.ok) return  // Non-fatal — keep polling

        const data = await resp.json()
        const status = data.status  // 'queued'|'running'|'completed'|'failed'

        // Update progress indicator from phase
        if (data.phase) setJobPhase(data.phase)

        if (status === 'completed') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setJobStatus('completed')
          setJobProgress(100)
          // Load recommendations
          await loadRecommendations(jobId)
          setActiveStep(3)
        } else if (status === 'failed') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setJobStatus('failed')
          setJobError(data.error || 'Analysis failed')
        } else {
          // Still running — estimate progress from phase
          const phaseProgress = {
            'loading_upstream':   10,
            'scanning_cobol':     25,
            'scanning_java':      50,
            'enriching':          70,
            'building_report':    85,
            'uploading':          95,
          }
          setJobProgress(phaseProgress[data.phase] ?? 40)
        }
      } catch (_) {
        // Non-fatal — keep polling
      }
    }

    poll()
    pollRef.current = setInterval(poll, 3000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [jobId, jobStatus, apiBase])

  // ── Start Analysis ────────────────────────────────────────────────────────

  const handleStartAnalysis = useCallback(async () => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    setJobStatus('starting')
    setJobError(null)
    setJobProgress(0)
    setJobPhase('')
    setRecommendations([])
    setRecsByFile({})
    setFilesByService({})
    setRecState({})
    setFinalized(false)
    setFinalizeResult(null)

    const patternsEnabled = Object.entries(enabledPatterns)
      .filter(([, on]) => on)
      .map(([id]) => id)

    const scope = scopeMode === 'all'
      ? null
      : [...selectedServices]

    try {
      const resp = await fetch(`${apiBase}/refactor-code/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: projectName,
          user_id:          userId,
          svcgen_job_id:    selectedJobId || null,
          options: {
            patterns_enabled: patternsEnabled,
            scope:            scope,
          },
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const data = await resp.json()
      const newJobId = data.job_id
      if (!newJobId) throw new Error('API did not return a job_id')

      setJobId(newJobId)
      setJobStatus('running')
      setJobProgress(5)

    } catch (e) {
      setJobStatus('failed')
      setJobError(e.message)
    }
  }, [apiBase, accountId, projectName, selectedJobId, scopeMode, selectedServices, enabledPatterns])

  // ── Load recommendations ──────────────────────────────────────────────────

  const loadRecommendations = useCallback(async (jid) => {
    setRecsLoading(true)
    setRecsError(null)
    try {
      const storedUser = getStoredUser()
      const userId = storedUser.user_id || storedUser.id || ''

      // Use the clean /recommendations endpoint — returns merged decisions + flat structure
      const resp = await fetch(
        `${apiBase}/refactor-code/${encodeURIComponent(jid)}/recommendations?user_id=${encodeURIComponent(userId)}`,
        { headers: getAuthHeaders() }
      )

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const payload = await resp.json()
      // payload: { job_id, summary, services: [{ service_name, files: [{ file_path, recommendations: [...] }] }] }

      const flat = []
      const byFile = {}
      const byService = {}
      const cobolRefs = {}
      const restoredDecisions = {}

      for (const svc of (payload.services || [])) {
        // Fix C: use friendlier fallback when service_name is empty/unknown
        const rawSvc = svc.service_name || ''
        const svcName = (!rawSvc || rawSvc === 'unknown') ? 'Unassigned' : rawSvc
        byService[svcName] = []
        for (const fileEntry of (svc.files || [])) {
          // Fix C: use friendlier fallback when file_path is empty/unknown
          const rawFile = fileEntry.file_path || ''
          const filename = (!rawFile || rawFile === 'unknown') ? 'Source mapping unavailable' : rawFile
          byService[svcName].push(filename)
          byFile[filename] = []
          for (const rec of (fileEntry.recommendations || [])) {
            const recId = rec.recommendation_id || rec.id || ''
            const normalized = {
              id:         recId,
              title:      rec.title || rec.smell_type || 'Recommendation',
              type:       rec.smell_type || 'OOP',
              confidence: Math.round((rec.confidence ?? 0.8) * 100),
              rationale:  rec.why || rec.rationale || '',
              before:     rec.before_snippet || '',
              after:      rec.after_snippet || '',
              cobolRef:   '',  // cobol_origin not included in /recommendations; available via /results/json if needed
            }
            flat.push(normalized)
            byFile[filename].push(normalized)
            // Restore user_decision from merged payload
            if (rec.user_decision === 'confirmed' || rec.user_decision === 'skipped') {
              restoredDecisions[recId] = rec.user_decision
            }
          }
        }
      }

      setRecommendations(flat)
      setRecsByFile(byFile)
      setFilesByService(byService)
      setCobolRefByFile(cobolRefs)

      // Set initial selected file
      const firstFile = Object.values(byService)[0]?.[0] || null
      setSelectedFile(firstFile)
      setExpandedServices(new Set(Object.keys(byService)))

      // Restore decisions from merged payload
      if (Object.keys(restoredDecisions).length > 0) {
        setRecState(restoredDecisions)
      }

    } catch (e) {
      setRecsError(e.message)
    } finally {
      setRecsLoading(false)
    }
  }, [apiBase])

  // ── Confirm / Skip decision ───────────────────────────────────────────────

  const handleDecision = useCallback(async (recId, decision) => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    // Optimistic update
    setRecState(prev => ({ ...prev, [recId]: decision }))
    setDecisionErrors(prev => { const n = { ...prev }; delete n[recId]; return n })

    try {
      const resp = await fetch(`${apiBase}/refactor-code/${encodeURIComponent(jobId)}/decision`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recommendation_id: recId,
          decision:          decision,
          user_id:           userId,
        }),
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }
      // Server confirmed — optimistic update stands
    } catch (e) {
      // Revert optimistic update on failure
      setRecState(prev => {
        const n = { ...prev }
        delete n[recId]
        return n
      })
      setDecisionErrors(prev => ({ ...prev, [recId]: e.message }))
    }
  }, [apiBase, jobId])

  // ── Undo decision ─────────────────────────────────────────────────────────

  const handleUndoDecision = useCallback(async (recId) => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    // Remove from local state immediately
    setRecState(prev => { const n = { ...prev }; delete n[recId]; return n })
    setDecisionErrors(prev => { const n = { ...prev }; delete n[recId]; return n })

    // Note: backend doesn't have a DELETE decision endpoint, so we use a no-op
    // approach — if needed in future, POST with decision='pending' could be added.
    // For now undo is local-only (consistent with session-level undo pattern).
  }, [])

  // ── Bulk decisions ────────────────────────────────────────────────────────

  // Sequential (not optimistic) — only marks a rec after backend confirms.
  // Tracks successes + failures; shows partial-failure banner when done.
  const handleBulkDecision = useCallback(async (recs, decision) => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    setBulkInProgress(true)
    setBulkResult(null)

    let succeeded = 0
    let failed = 0
    let firstFailId  = null
    let firstFailMsg = null

    for (const rec of recs) {
      try {
        const resp = await fetch(
          `${apiBase}/refactor-code/${encodeURIComponent(jobId)}/decision`,
          {
            method: 'POST',
            headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ recommendation_id: rec.id, decision, user_id: userId }),
          }
        )
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
          throw new Error(err.detail || `HTTP ${resp.status}`)
        }
        // Only mark in local state AFTER backend confirms — never optimistic for bulk
        setRecState(prev => ({ ...prev, [rec.id]: decision }))
        succeeded++
      } catch (e) {
        failed++
        if (!firstFailId) { firstFailId = rec.id; firstFailMsg = e.message }
      }
    }

    setBulkInProgress(false)
    setBulkDialog(null)
    setBulkResult({ succeeded, failed, firstFailId, firstFailMsg })
  }, [apiBase, jobId])

  // Derive pending targets → open confirm dialog
  const openBulkDialog = useCallback((decision, scope) => {
    // scope = 'visible' (all pending in file) or a pattern type string
    const pending = fileRecs.filter(r => !recState[r.id])
    const targets = scope === 'visible' ? pending : pending.filter(r => r.type === scope)
    if (targets.length === 0) return

    const countsByType = {}
    targets.forEach(r => { countsByType[r.type] = (countsByType[r.type] || 0) + 1 })

    setBulkResult(null)
    setBulkDialog({ decision, scope, targets, countsByType })
  }, [fileRecs, recState])

  // ── Finalize ──────────────────────────────────────────────────────────────

  const handleFinalize = useCallback(async () => {
    if (finalized || finalizing || confirmedCount === 0) return

    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    setFinalizing(true)
    setFinalizeError(null)

    try {
      const resp = await fetch(
        `${apiBase}/refactor-code/${encodeURIComponent(jobId)}/finalize`,
        {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ user_id: userId }),
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }
      const data = await resp.json()
      setFinalized(true)
      setFinalizeResult({
        confirmed_count: data.summary?.confirmed ?? confirmedCount,
        skipped_count:   data.summary?.skipped   ?? skippedCount,
      })
    } catch (e) {
      setFinalizeError(e.message)
    } finally {
      setFinalizing(false)
    }
  }, [apiBase, jobId, finalized, finalizing, confirmedCount, skippedCount])

  // ── Refine Code: Start ────────────────────────────────────────────────────

  const handleStartRefineCode = useCallback(async () => {
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    setRfneStatus('starting')
    setRfneError(null)
    setRfnePatchCounts(null)
    setRfnePatchReport(null)
    setRfneCompileReport(null)

    try {
      const resp = await fetch(`${apiBase}/refine-code/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: projectName,
          user_id:          userId,
          refactor_job_id:  jobId,
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const data = await resp.json()
      if (!data.job_id) throw new Error('API did not return a job_id')

      setRfneJobId(data.job_id)
      setRfneStatus('polling')
      setActiveStep(5)

    } catch (e) {
      setRfneStatus('failed')
      setRfneError(e.message)
    }
  }, [apiBase, accountId, projectName, jobId])

  // ── Refine Code: Poll ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!rfneJobId || rfneStatus !== 'polling') return

    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''

    const poll = async () => {
      try {
        const resp = await fetch(
          `${apiBase}/refine-code/status/${encodeURIComponent(rfneJobId)}`,
          { headers: getAuthHeaders() }
        )
        if (!resp.ok) return  // Non-fatal — keep polling

        const data = await resp.json()
        const s = data.status  // normalized: 'queued'|'running'|'completed'|'failed'|'compile_error'

        // Update patch counts from live status response
        setRfnePatchCounts({
          applied:        data.patches_applied  ?? null,
          failed:         data.patches_failed   ?? null,
          compile_passed: data.compile_passed   ?? null,
          compile_errors: data.compile_errors   ?? null,
        })

        if (s === 'completed') {
          clearInterval(rfnePollRef.current)
          rfnePollRef.current = null
          if (data.package_uri) setRfnePackageUri(data.package_uri)
          setRfneStatus('succeeded')
          fetchRfneReports(rfneJobId, userId, false)
        } else if (s === 'compile_error') {
          clearInterval(rfnePollRef.current)
          rfnePollRef.current = null
          if (data.package_uri) setRfnePackageUri(data.package_uri)
          setRfneStatus('compile_error')
          fetchRfneReports(rfneJobId, userId, true)
        } else if (s === 'failed') {
          clearInterval(rfnePollRef.current)
          rfnePollRef.current = null
          setRfneStatus('failed')
          setRfneError(data.error || 'Refine Code job failed')
        }
        // queued/running: keep polling
      } catch (_) {
        // Non-fatal — keep polling
      }
    }

    poll()
    rfnePollRef.current = setInterval(poll, 3000)
    return () => {
      if (rfnePollRef.current) clearInterval(rfnePollRef.current)
    }
  }, [rfneJobId, rfneStatus, apiBase])

  // ── Refine Code: Fetch reports from S3 ───────────────────────────────────

  const fetchRfneReports = useCallback(async (jid, userId, fetchCompile) => {
    setRfneReportLoading(true)
    try {
      // Always fetch patch_report.json
      const patchResp = await fetch(
        `${apiBase}/refine-code/results/json/${encodeURIComponent(jid)}/patch_report.json`,
        { headers: getAuthHeaders() }
      )
      if (patchResp.ok) {
        const pr = await patchResp.json()
        setRfnePatchReport(pr)
      }

      // Fetch compile_report.json on compile failure
      if (fetchCompile) {
        const compResp = await fetch(
          `${apiBase}/refine-code/results/json/${encodeURIComponent(jid)}/compile_report.json`,
          { headers: getAuthHeaders() }
        )
        if (compResp.ok) {
          const cr = await compResp.json()
          setRfneCompileReport(cr)
        }
      }
    } catch (_) {
      // Non-fatal — status counts from poll are still shown
    } finally {
      setRfneReportLoading(false)
    }
  }, [apiBase])

  // ── Java Packaging handoff ────────────────────────────────────────────────

  // ── Refine Code: Download ZIP package ────────────────────────────────────
  const handleDownloadRefinedCode = useCallback(async () => {
    if (!rfneJobId) return
    setRfneDownloading(true)
    try {
      const resp = await fetch(
        `${apiBase}/refine-code/results/download/${encodeURIComponent(rfneJobId)}/refined-code-package`,
        { headers: getAuthHeaders() }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }
      const blob = await resp.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      // Try to get filename from Content-Disposition header
      const cd   = resp.headers.get('Content-Disposition') || ''
      const match = cd.match(/filename="?([^"]+)"?/)
      a.download = match ? match[1] : `refined_code_${rfneJobId}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('[RefactorCodeView] Download failed:', e)
      alert(`Download failed: ${e.message}`)
    } finally {
      setRfneDownloading(false)
    }
  }, [apiBase, rfneJobId])

  const handleGoToJavaPkg = useCallback(() => {
    // Pass the SvcGen job ID selected in Step 1 so JPkg receives a non-empty
    // svcgen_job_ids array. rfneJobId is threaded alongside as the second arg.
    // App.jsx wires onGoToJavaPkg → (jobIds, rfneId) => { setSvcGenJobIds(jobIds); setRfneJobId(rfneId); setMode('journey-jpkg') }
    const svcgenIds = selectedJobId ? [selectedJobId] : []
    onGoToJavaPkg?.(svcgenIds, rfneJobId)
  }, [onGoToJavaPkg, rfneJobId, selectedJobId])

  // ── Helpers ───────────────────────────────────────────────────────────────

  const toggleService = (s) => {
    setSelectedServices(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }

  const toggleFolder = (svc) => {
    setExpandedServices(prev => {
      const next = new Set(prev)
      if (next.has(svc)) next.delete(svc)
      else next.add(svc)
      return next
    })
  }

  // ── Step sidebar ──────────────────────────────────────────────────────────

  const renderStepItem = (step) => {
    const isDone   = step.id < activeStep
    const isActive = step.id === activeStep
    const isLocked = step.id > activeStep

    return (
      <button
        key={step.id}
        className={`gd-step-btn ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isLocked ? 'locked' : ''}`}
        onClick={() => !isLocked && setActiveStep(step.id)}
        disabled={isLocked}
      >
        <div className="gd-step-icon">
          {isDone   ? <CheckCircle2 size={16} />
          : isLocked ? <Lock size={14} />
          :             <Circle size={16} />}
        </div>
        <div className="gd-step-text">
          <span className="gd-step-label">{step.label}</span>
          <span className="gd-step-desc">{step.desc}</span>
        </div>
      </button>
    )
  }

  // ── STEP 1: Select Scope ──────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="gd-step-content">
      <div className="gd-step-header">
        <h2 className="gd-step-title">Select Scope</h2>
        <p className="gd-step-subtitle">
          Choose which Service Generation run and services to include in the refactor analysis
          for <strong>{projectName}</strong>.
        </p>
      </div>

      {/* SvcGen job picker */}
      <div className="gd-section">
        <h3 className="gd-section-label">
          Service Generation Run
          {svcgenLoading && <Loader2 size={13} className="rc-spin-icon" style={{ marginLeft: 8 }} />}
        </h3>

        {svcgenError && (
          <div className="rc-inline-error">
            <AlertCircle size={14} /> {svcgenError}
            <button className="rc-retry-btn" onClick={loadSvcgenJobs}>Retry</button>
          </div>
        )}

        {!svcgenLoading && !svcgenError && svcgenJobs.length === 0 && (
          <div className="rc-inline-error">
            <AlertCircle size={14} />
            No completed Service Generation jobs found for <strong>{projectName}</strong>.
            Run Build Services first.
          </div>
        )}

        {svcgenJobs.length > 0 && (
          <div className="rc-svcgen-job-list">
            {(showAllJobs ? svcgenJobs : svcgenJobs.slice(0, 5)).map(j => {
              const isSelected = selectedJobId === j.job_id
              return (
                <button
                  key={j.job_id}
                  className={`rc-scope-card rc-job-item ${isSelected ? 'selected rc-job-item--selected' : ''}`}
                  onClick={() => setSelectedJobId(j.job_id)}
                >
                  <div className="rc-scope-card-check">
                    {isSelected ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                  </div>
                  <div className="rc-scope-card-text">
                    <span className="rc-scope-card-name rc-job-label">
                      {j.label || j.application_name || j.job_id}
                    </span>
                    <span className="rc-scope-card-desc rc-job-id">
                      {j.job_id}
                    </span>
                  </div>
                </button>
              )
            })}
            {svcgenJobs.length > 5 && (
              <button
                className="rc-show-older-btn"
                onClick={() => setShowAllJobs(v => !v)}
              >
                {showAllJobs
                  ? 'Collapse older runs ▲'
                  : `Show ${svcgenJobs.length - 5} older run${svcgenJobs.length - 5 !== 1 ? 's' : ''} ▼`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Visual separator between run picker and scope section */}
      <hr className="rc-scope-separator" />

      {/* Scope mode */}
      <div className="gd-section">
        <h3 className="gd-section-label">Services to Analyze</h3>
        <div className="rc-scope-options">
          {[
            { id: 'all',      label: 'All Services',      desc: 'Analyze all generated Java services' },
            { id: 'selected', label: 'Selected Services', desc: 'Choose specific services below' },
          ].map(opt => (
            <div key={opt.id}>
              <button
                className={`rc-scope-card ${scopeMode === opt.id ? 'selected' : ''}`}
                onClick={() => setScopeMode(opt.id)}
              >
                <div className="rc-scope-card-check">
                  {scopeMode === opt.id ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                </div>
                <div className="rc-scope-card-text">
                  <span className="rc-scope-card-name">{opt.label}</span>
                  <span className="rc-scope-card-desc">{opt.desc}</span>
                </div>
              </button>
              {opt.id === 'selected' && scopeMode === 'selected' && serviceList.length === 0 && (
                <p className="rc-scope-subtext">
                  Service inventory will be loaded from the selected Service Generation run.
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Service list — shown when services known */}
      {serviceList.length > 0 && (
        <div className="gd-section">
          <h3 className="gd-section-label">Services</h3>
          <div className="rc-service-list">
            {serviceList.map(svc => (
              <div
                key={svc}
                className={`rc-service-row ${scopeMode === 'selected' && !selectedServices.has(svc) ? 'dimmed' : ''}`}
              >
                {scopeMode === 'selected' && (
                  <input
                    type="checkbox"
                    className="rc-service-check"
                    checked={selectedServices.has(svc)}
                    onChange={() => toggleService(svc)}
                  />
                )}
                <FolderOpen size={14} className="rc-service-icon" />
                <span className="rc-service-name">{svc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // ── STEP 2: Configure ─────────────────────────────────────────────────────

  const renderStep2 = () => {
    const isRunning  = jobStatus === 'starting' || jobStatus === 'running'
    const isComplete = jobStatus === 'completed'

    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">Configure</h2>
          <p className="gd-step-subtitle">
            Choose which refactor pattern types to apply. Each enabled type generates
            recommendations for the selected services.
          </p>
          <p className="rc-pattern-helper">
            <strong>MVC Alignment</strong> is disabled by default because it can require
            larger framework-level restructuring. Other refactor checks remain enabled.
          </p>
        </div>

        <div className="rc-pattern-list">
          {PATTERNS.map(p => {
            const PIcon = p.icon
            const on    = enabledPatterns[p.id]
            return (
              <div key={p.id} className={`rc-pattern-card ${on ? 'on' : ''}`}>
                <div className="rc-pattern-iconbox">
                  <PIcon size={18} />
                </div>
                <div className="rc-pattern-text">
                  <span className="rc-pattern-label">
                    {p.label}
                    {p.advanced && <span className="rc-pattern-advanced-badge">Advanced</span>}
                  </span>
                  <span className="rc-pattern-desc">{p.desc}</span>
                </div>
                <button
                  className={`rc-toggle-btn ${on ? 'on' : ''}`}
                  onClick={() => setEnabledPatterns(prev => ({ ...prev, [p.id]: !on }))}
                  title={on ? 'Disable' : 'Enable'}
                  disabled={isRunning}
                >
                  {on ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
                </button>
              </div>
            )
          })}
        </div>

        {/* Analysis start / progress */}
        <div className="rc-analysis-launch">
          {jobError && (
            <div className="rc-inline-error">
              <AlertCircle size={14} /> {jobError}
            </div>
          )}

          {isRunning && (
            <div className="rc-analysis-progress">
              <div className="rc-progress-bar-wrap">
                <div className="rc-progress-bar" style={{ width: `${jobProgress}%` }} />
              </div>
              <div className="rc-progress-label">
                <Loader2 size={13} className="rc-spin-icon" />
                {jobPhase
                  ? `Analyzing — ${jobPhase.replace(/_/g, ' ')}…`
                  : 'Analysis in progress…'}
                {' '}{jobProgress > 0 ? `${jobProgress}%` : ''}
              </div>
            </div>
          )}

          {isComplete && (
            <div className="rc-analysis-done-note">
              <CheckCircle2 size={14} />
              Analysis complete — recommendations loaded. Continue to Step 3 to review.
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── STEP 3: Review Recommendations — full workspace ───────────────────────

  const renderStep3 = () => {
    if (recsLoading) {
      return (
        <div className="rc-workspace rc-workspace-loading">
          <Loader2 size={24} className="rc-spin-icon" />
          <p>Loading recommendations…</p>
        </div>
      )
    }

    if (recsError) {
      return (
        <div className="rc-workspace rc-workspace-loading">
          <AlertCircle size={20} />
          <p>Failed to load recommendations: {recsError}</p>
          <button className="rc-retry-btn" onClick={() => loadRecommendations(jobId)}>
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )
    }

    return (<>
      <div className="rc-workspace">

        {/* LEFT: Java file tree */}
        <aside className="rc-file-panel">
          <div className="rc-file-panel-header">
            <span>JAVA FILES</span>
          </div>
          <div className="rc-file-tree">
            {activeServices.map(svc => {
              const isOpen = expandedServices.has(svc)
              const files  = filesByService[svc] || []
              return (
                <div key={svc} className="rc-tree-service">
                  <button
                    className="rc-tree-folder-btn"
                    onClick={() => toggleFolder(svc)}
                  >
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <FolderOpen size={14} className="rc-tree-folder-icon" />
                    <span className="rc-tree-folder-name">{svc}</span>
                  </button>
                  {isOpen && files.map(filename => {
                    const isSelected = selectedFile === filename
                    const recs = recsByFile[filename] || []
                    const confirmedHere = recs.filter(r => recState[r.id] === 'confirmed').length
                    const pendingHere   = recs.filter(r => !recState[r.id]).length
                    return (
                      <div key={filename}>
                        <button
                          className={`rc-tree-file-btn ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedFile(filename)}
                        >
                          <FileCode size={13} className="rc-tree-file-icon" />
                          <div className="rc-tree-file-text">
                            <span className="rc-tree-file-name">{filename}</span>
                            <span className="rc-tree-cobol-ref">{cobolRefByFile[filename] || ''}</span>
                          </div>
                          {pendingHere > 0 && (
                            <span className="rc-tree-file-badge pending">{pendingHere}</span>
                          )}
                          {pendingHere === 0 && recs.length > 0 && (
                            <span className="rc-tree-file-badge done"><CheckCircle2 size={11} /></span>
                          )}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </aside>

        {/* CENTER: Recommendation cards */}
        <main className="rc-rec-panel">
          <div className="rc-rec-panel-header">
            <span className="rc-rec-panel-filename">{selectedFile || '—'}</span>
            <span className="rc-rec-panel-count">
              {fileRecs.length} recommendation{fileRecs.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* BULK ACTION TOOLBAR */}
          {!finalized && fileRecs.length > 0 && (() => {
            const pendingVisible = fileRecs.filter(r => !recState[r.id])
            const pendingTypes   = [...new Set(pendingVisible.map(r => r.type))]
            const patternPending = bulkPattern
              ? pendingVisible.filter(r => r.type === bulkPattern).length
              : 0
            return (
              <div className="rc-bulk-toolbar">
                <div className="rc-bulk-toolbar-group">
                  <button
                    className="rc-bulk-btn confirm"
                    disabled={pendingVisible.length === 0 || bulkInProgress}
                    onClick={() => openBulkDialog('confirmed', 'visible')}
                    title="Confirm all pending recommendations in this file"
                  >
                    <CheckCircle2 size={13} />
                    Confirm All{pendingVisible.length > 0 ? ` (${pendingVisible.length})` : ''}
                  </button>
                  <button
                    className="rc-bulk-btn skip"
                    disabled={pendingVisible.length === 0 || bulkInProgress}
                    onClick={() => openBulkDialog('skipped', 'visible')}
                    title="Skip all pending recommendations in this file"
                  >
                    Skip All{pendingVisible.length > 0 ? ` (${pendingVisible.length})` : ''}
                  </button>
                </div>
                {pendingTypes.length > 1 && (
                  <div className="rc-bulk-toolbar-group">
                    <select
                      className="rc-bulk-pattern-select"
                      value={bulkPattern}
                      onChange={e => setBulkPattern(e.target.value)}
                    >
                      <option value="">Pattern…</option>
                      {pendingTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      className="rc-bulk-btn confirm"
                      disabled={!bulkPattern || patternPending === 0 || bulkInProgress}
                      onClick={() => openBulkDialog('confirmed', bulkPattern)}
                      title={bulkPattern ? `Confirm all pending ${bulkPattern} recommendations` : ''}
                    >
                      <CheckCircle2 size={13} />
                      Confirm{patternPending > 0 ? ` (${patternPending})` : ''}
                    </button>
                    <button
                      className="rc-bulk-btn skip"
                      disabled={!bulkPattern || patternPending === 0 || bulkInProgress}
                      onClick={() => openBulkDialog('skipped', bulkPattern)}
                      title={bulkPattern ? `Skip all pending ${bulkPattern} recommendations` : ''}
                    >
                      Skip{patternPending > 0 ? ` (${patternPending})` : ''}
                    </button>
                  </div>
                )}
              </div>
            )
          })()}

          {/* BULK RESULT BANNER */}
          {bulkResult && (
            <div className={`rc-bulk-result ${bulkResult.failed > 0 ? 'partial' : 'success'}`}>
              {bulkResult.failed === 0 ? (
                <><CheckCircle2 size={13} /> {bulkResult.succeeded} updated successfully</>
              ) : (
                <><AlertCircle size={13} /> {bulkResult.succeeded} succeeded, {bulkResult.failed} failed — {bulkResult.firstFailMsg}</>
              )}
              <button className="rc-bulk-result-close" onClick={() => setBulkResult(null)}>×</button>
            </div>
          )}

          <div className="rc-rec-scroll">
            {fileRecs.length === 0 ? (
              <div className="rc-rec-empty">
                <AlertCircle size={20} className="rc-rec-empty-icon" />
                <p>No recommendations for this file with current pattern settings.</p>
              </div>
            ) : (
              fileRecs.map(rec => {
                const state    = recState[rec.id]
                const isConf   = state === 'confirmed'
                const isSkip   = state === 'skipped'
                const colorKey = TYPE_COLOR[rec.type] || 'blue'
                const decError = decisionErrors[rec.id]
                return (
                  <div
                    key={rec.id}
                    className={`rc-rec-card ${isConf ? 'confirmed' : ''} ${isSkip ? 'skipped' : ''}`}
                  >
                    <div className="rc-rec-card-header">
                      <span className="rc-rec-title">{rec.title}</span>
                      <div className="rc-rec-card-meta">
                        <span className={`rc-type-badge ${colorKey}`}>{rec.type}</span>
                        <span className="rc-confidence">{rec.confidence}% confidence</span>
                      </div>
                    </div>

                    <p className="rc-rec-rationale">
                      <span className="rc-rec-rationale-label">Why: </span>
                      {rec.rationale}
                    </p>

                    {(rec.before || rec.after) && (
                      <div className="rc-code-blocks">
                        {rec.before && (
                          <div className="rc-code-block">
                            <div className="rc-code-block-label before">Before</div>
                            <pre className="rc-code-pre">{rec.before}</pre>
                          </div>
                        )}
                        {rec.after && (
                          <div className="rc-code-block">
                            <div className="rc-code-block-label after">After</div>
                            <pre className="rc-code-pre">{rec.after}</pre>
                          </div>
                        )}
                      </div>
                    )}

                    {decError && (
                      <div className="rc-inline-error" style={{ marginTop: 8 }}>
                        <AlertCircle size={13} /> {decError}
                      </div>
                    )}

                    {(isConf || isSkip) ? (
                      <div className={`rc-rec-state-banner ${isConf ? 'confirmed' : 'skipped'}`}>
                        {isConf ? (
                          <><CheckCircle2 size={14} /> Confirmed — will be included in the refactor plan</>
                        ) : (
                          <><AlertCircle size={14} /> Skipped — not included</>
                        )}
                        {!finalized && (
                          <button
                            className="rc-rec-undo-btn"
                            onClick={() => handleUndoDecision(rec.id)}
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="rc-rec-actions">
                        <button
                          className="rc-rec-confirm-btn"
                          onClick={() => handleDecision(rec.id, 'confirmed')}
                          disabled={finalized}
                        >
                          <CheckCircle2 size={14} />
                          Confirm
                        </button>
                        <button
                          className="rc-rec-skip-btn"
                          onClick={() => handleDecision(rec.id, 'skipped')}
                          disabled={finalized}
                        >
                          Skip
                        </button>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </main>

        {/* RIGHT: Grounding + Summary */}
        <aside className="rc-grounding-panel">
          <p className="rc-grounding-heading">GROUNDING</p>
          {selectedFile && (
            <div className="rc-grounding-block">
              <span className="rc-grounding-label">COBOL Origin</span>
              <span className="rc-grounding-value">{cobolRefByFile[selectedFile] || '—'}</span>
            </div>
          )}
          {fileRecs.length > 0 && (
            <div className="rc-grounding-block">
              <span className="rc-grounding-label">Pattern Types</span>
              <div className="rc-grounding-types">
                {[...new Set(fileRecs.map(r => r.type))].map(t => (
                  <span key={t} className={`rc-type-badge ${TYPE_COLOR[t] || 'blue'}`}>{t}</span>
                ))}
              </div>
            </div>
          )}

          <div className="rc-grounding-divider" />
          <p className="rc-grounding-heading">SESSION SUMMARY</p>
          <div className="rc-grounding-block">
            <span className="rc-grounding-label">Confirmed</span>
            <span className="rc-grounding-value confirmed">{confirmedCount}</span>
          </div>
          <div className="rc-grounding-block">
            <span className="rc-grounding-label">Skipped</span>
            <span className="rc-grounding-value skipped">{skippedCount}</span>
          </div>
          <div className="rc-grounding-block">
            <span className="rc-grounding-label">Pending</span>
            <span className="rc-grounding-value">{pendingCount}</span>
          </div>

          {/* Continue button sits directly under Session Summary counts —
              always visible without scrolling, even with many confirmed items. */}
          {activeStep === 3 && (
            <button
              className="rc-workspace-continue-btn"
              onClick={() => setActiveStep(4)}
            >
              Continue to Review Plan
              <ChevronRight size={14} />
            </button>
          )}

          {confirmedCount > 0 && (
            <>
              <div className="rc-grounding-divider" />
              <p className="rc-grounding-heading">CONFIRMED SO FAR</p>
              <div className="rc-confirmed-list">
                {confirmedRecs.map(r => (
                  <div key={r.id} className="rc-confirmed-item">
                    <CheckCircle2 size={11} className="rc-confirmed-icon" />
                    <div className="rc-confirmed-text">
                      <span className="rc-confirmed-title">{r.title}</span>
                      <span className={`rc-type-badge xs ${TYPE_COLOR[r.type] || 'blue'}`}>{r.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>

      {/* BULK CONFIRM DIALOG */}
      {bulkDialog && (
        <div className="rc-bulk-overlay">
          <div className="rc-bulk-dialog">
            <div className="rc-bulk-dialog-header">
              <span className="rc-bulk-dialog-title">
                {bulkDialog.decision === 'confirmed' ? 'Confirm' : 'Skip'}{' '}
                {bulkDialog.scope === 'visible' ? 'All Visible' : bulkDialog.scope}{' '}
                Recommendations
              </span>
            </div>
            <div className="rc-bulk-dialog-body">
              <p className="rc-bulk-dialog-summary">
                <strong>{bulkDialog.targets.length}</strong> pending recommendation{bulkDialog.targets.length !== 1 ? 's' : ''} will be{' '}
                <strong>{bulkDialog.decision === 'confirmed' ? 'confirmed' : 'skipped'}</strong>:
              </p>
              <div className="rc-bulk-dialog-counts">
                {Object.entries(bulkDialog.countsByType).map(([type, count]) => (
                  <div key={type} className="rc-bulk-dialog-count-row">
                    <span className={`rc-type-badge ${TYPE_COLOR[type] || 'blue'}`}>{type}</span>
                    <span className="rc-bulk-dialog-count-val">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rc-bulk-dialog-actions">
              <button
                className="rc-bulk-dialog-cancel"
                onClick={() => setBulkDialog(null)}
                disabled={bulkInProgress}
              >
                Cancel
              </button>
              <button
                className={`rc-bulk-dialog-ok ${bulkDialog.decision === 'confirmed' ? 'confirm' : 'skip'}`}
                onClick={() => handleBulkDecision(bulkDialog.targets, bulkDialog.decision)}
                disabled={bulkInProgress}
              >
                {bulkInProgress
                  ? <><Loader2 size={13} className="rc-spin-icon" /> Working…</>
                  : bulkDialog.decision === 'confirmed'
                    ? <><CheckCircle2 size={13} /> Confirm All</>
                    : 'Skip All'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
    )
  }

  // ── STEP 4: Review Plan ───────────────────────────────────────────────────

  const renderStep4 = () => {
    const skippedRecs = allRecs.filter(r => skippedIds.includes(r.id))

    // Build lookup: rec.id → service name and filename
    const recToService = {}
    const recToFile    = {}
    for (const [svc, files] of Object.entries(filesByService)) {
      for (const filename of files) {
        for (const rec of (recsByFile[filename] || [])) {
          recToFile[rec.id]    = filename
          recToService[rec.id] = svc
        }
      }
    }

    const canFinalize = confirmedCount > 0 && !finalized && !finalizing

    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">Review Plan</h2>
          <p className="gd-step-subtitle">
            {finalized && finalizeResult
              ? `Analysis finalized. ${finalizeResult.confirmed_count} recommendation${finalizeResult.confirmed_count !== 1 ? 's' : ''} confirmed.`
              : confirmedCount > 0
              ? `${confirmedCount} recommendation${confirmedCount !== 1 ? 's' : ''} confirmed for the refactor run.`
              : 'No recommendations confirmed yet. Go back to Review to confirm at least one.'}
          </p>
        </div>

        {/* Action row — moved above the table so Back / Finalize Analysis are
            always visible without scrolling, even with a long confirmed list. */}

        {/* Confirmation note — shown when already finalized */}
        {finalized && (
          <div className="rc-finalize-done" style={{ marginBottom: 4 }}>
            <CheckCircle2 size={16} />
            {finalizeResult?.confirmed_count ?? confirmedCount} recommendation{(finalizeResult?.confirmed_count ?? confirmedCount) !== 1 ? 's' : ''} confirmed. Ready to apply.
          </div>
        )}

        {/* Finalize CTA */}
        <div className="rc-plan-launch" style={{ paddingBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
          <button
            className="gd-footer-btn secondary"
            onClick={() => setActiveStep(3)}
            disabled={finalized}
          >
            Back
          </button>

          <div className="rc-run-refine-wrap">
            {finalizeError && (
              <div className="rc-inline-error" style={{ marginBottom: 8 }}>
                <AlertCircle size={14} /> {finalizeError}
              </div>
            )}

            {finalized ? (
              <button
                className="rc-run-refine-btn"
                onClick={() => { setRfneStatus('idle'); setActiveStep(5) }}
              >
                <Play size={15} />
                Apply Refactor Plan
              </button>
            ) : (
              <button
                className={`rc-run-refine-btn ${!canFinalize ? 'disabled' : ''}`}
                onClick={handleFinalize}
                disabled={!canFinalize}
                title={confirmedCount === 0 ? 'Confirm at least one recommendation first' : ''}
              >
                {finalizing
                  ? <><Loader2 size={15} className="rc-spin-icon" /> Finalizing…</>
                  : <><Play size={15} /> Finalize Analysis</>
                }
              </button>
            )}
          </div>
        </div>

        {/* Confirmed table */}
        <div className="gd-section">
          <h3 className="gd-section-label">
            Confirmed Recommendations
            {confirmedCount > 0 && (
              <span className="gd-section-count">{confirmedCount}</span>
            )}
          </h3>
          {confirmedCount > 0 ? (
            <div className="rc-plan-table-wrap">
              <table className="rc-plan-table">
                <thead>
                  <tr>
                    <th>Recommendation</th>
                    <th>Service</th>
                    <th>File</th>
                    <th>Type</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmedRecs.map(r => (
                    <tr key={r.id}>
                      <td className="rc-plan-title">{r.title}</td>
                      <td className="rc-plan-svc">{recToService[r.id] || '—'}</td>
                      <td className="rc-plan-file">{recToFile[r.id] || '—'}</td>
                      <td>
                        <span className={`rc-type-badge ${TYPE_COLOR[r.type] || 'blue'}`}>{r.type}</span>
                      </td>
                      <td className="rc-plan-conf">{r.confidence}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rc-plan-empty">
              No confirmed recommendations — go back and confirm at least one.
            </div>
          )}
        </div>

        {/* Skipped — collapsible */}
        {skippedCount > 0 && (
          <div className="gd-section">
            <button
              className="rc-plan-collapsed-toggle"
              onClick={() => setExpandedSkipped(v => !v)}
            >
              <span className="gd-section-label">
                Skipped Recommendations
                <span className="gd-section-count">{skippedCount}</span>
              </span>
              {expandedSkipped ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {expandedSkipped && (
              <div className="rc-skipped-list">
                {skippedRecs.map(r => (
                  <div key={r.id} className="rc-skipped-row">
                    <span className="rc-skipped-title">{r.title}</span>
                    <span className="rc-skipped-file">{recToFile[r.id]}</span>
                    <span className={`rc-type-badge ${TYPE_COLOR[r.type] || 'blue'}`}>{r.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ── STEP 5: Refine Code ───────────────────────────────────────────────────

  const renderStep5 = () => {
    const isPolling = rfneStatus === 'polling' || rfneStatus === 'starting'
    const isSucceeded = rfneStatus === 'succeeded'
    const isCompileError = rfneStatus === 'compile_error'
    const isFailed = rfneStatus === 'failed'
    const isDone = isSucceeded || isCompileError

    // Patch report summary: merge live counts with detailed report when available
    // NOTE: patch_report.json uses "skipped_manual_only" not "skipped"
    const applied       = rfnePatchReport?.summary?.applied           ?? rfnePatchCounts?.applied       ?? null
    const annotated     = rfnePatchReport?.summary?.annotated         ?? null
    const skipped       = rfnePatchReport?.summary?.skipped_manual_only ?? rfnePatchReport?.summary?.skipped ?? null
    const patchFailed   = rfnePatchReport?.summary?.failed            ?? rfnePatchCounts?.failed        ?? null
    const manualReview  = rfnePatchReport?.summary?.manual_review     ?? rfnePatchReport?.summary?.todo ?? null
    const compilePassed = rfnePatchReport?.summary?.compile_passed    ?? rfnePatchCounts?.compile_passed ?? null

    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">Apply Refactor Plan</h2>
          <p className="gd-step-subtitle">
            {isPolling && 'Applying confirmed recommendations to generated Java files and running compile check…'}
            {isSucceeded && 'Refactor plan applied. Download the refined code package or continue to Build / Package.'}
            {isCompileError && 'Patches applied but compile check found errors. Review below.'}
            {isFailed && 'Refine Code job failed. See the error below.'}
            {rfneStatus === 'idle' && `${finalizeResult?.confirmed_count ?? confirmedCount} recommendation${(finalizeResult?.confirmed_count ?? confirmedCount) !== 1 ? 's' : ''} confirmed. Ready to apply.`}
          </p>
        </div>

        {/* Error banner */}
        {isFailed && rfneError && (
          <div className="rc-inline-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={14} /> {rfneError}
          </div>
        )}

        {/* Polling status panel */}
        {isPolling && (
          <div className="rc-refine-polling-panel">
            <div className="rc-refine-polling-header">
              <Loader2 size={16} className="rc-spin-icon" />
              <span>Running Refine Code…</span>
            </div>
            {rfnePatchCounts && (rfnePatchCounts.applied !== null || rfnePatchCounts.failed !== null) && (
              <div className="rc-refine-counts">
                {rfnePatchCounts.applied !== null && (
                  <div className="rc-refine-count-row">
                    <CheckCircle2 size={13} className="rc-refine-icon-applied" />
                    <span>{rfnePatchCounts.applied} patches applied</span>
                  </div>
                )}
                {rfnePatchCounts.failed !== null && rfnePatchCounts.failed > 0 && (
                  <div className="rc-refine-count-row">
                    <XCircle size={13} className="rc-refine-icon-failed" />
                    <span>{rfnePatchCounts.failed} patches failed</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results panel split into two blocks so the CTA row can sit between
            the summary/compile info and the per-file table. No button duplication:
            the single CTA row renders once, between the two isDone sections. */}

        {/* Results — Part 1: summary cards + callouts + compile status */}
        {isDone && (
          <div className="rc-refine-results">

            {/* Patch summary cards */}
            <div className="rc-refine-summary-row">
              {applied !== null && (
                <div className="rc-refine-stat-card applied">
                  <span className="rc-refine-stat-num">{applied}</span>
                  <span className="rc-refine-stat-lbl">Patches Applied</span>
                </div>
              )}
              {manualReview !== null && (
                <div className="rc-refine-stat-card todo">
                  <span className="rc-refine-stat-num">{manualReview}</span>
                  <span className="rc-refine-stat-lbl">Manual Review Required</span>
                </div>
              )}
              {annotated !== null && (
                <div className="rc-refine-stat-card annotated">
                  <span className="rc-refine-stat-num">{annotated}</span>
                  <span className="rc-refine-stat-lbl">Annotated</span>
                </div>
              )}
              {skipped !== null && (
                <div className="rc-refine-stat-card skipped">
                  <span className="rc-refine-stat-num">{skipped}</span>
                  {/* Fix E: rename "Skipped" to "Needs Review" — skipped sounds like failure */}
                  <span className="rc-refine-stat-lbl">Needs Review</span>
                </div>
              )}
              {patchFailed !== null && patchFailed > 0 && (
                <div className="rc-refine-stat-card failed">
                  <span className="rc-refine-stat-num">{patchFailed}</span>
                  <span className="rc-refine-stat-lbl">Failed</span>
                </div>
              )}
            </div>

            {/* Manual review callout — not failures */}
            {manualReview !== null && manualReview > 0 && (
              <div className="rc-refine-todo-note">
                <AlertCircle size={13} />
                <span>
                  <strong>{manualReview} recommendation{manualReview !== 1 ? 's' : ''}</strong> require manual review — TODO comments have been inserted in the generated Java files at the relevant locations. These are not failures.
                </span>
              </div>
            )}

            {/* Fix E: callout for items needing attention (skipped or annotated) */}
            {((skipped !== null && skipped > 0) || (annotated !== null && annotated > 0)) && (
              <div className="rc-refine-todo-note" style={{ marginTop: 8 }}>
                <AlertCircle size={13} />
                <span>
                  Some recommendations could not be automatically applied. See the patch report below for details. These items have been marked for manual review in the generated code.
                </span>
              </div>
            )}

            {/* Compile status */}
            {compilePassed !== null && (
              <div className={`rc-refine-compile-badge ${compilePassed ? 'passed' : 'warn'}`}>
                {compilePassed
                  ? <><CheckCircle2 size={14} /> Compile passed</>
                  /* Fix E: soften failed compile badge — may be pre-existing */
                  : <><AlertCircle size={14} /> Compile check found errors — may include pre-existing issues</>
                }
              </div>
            )}

            {/* Compile error detail */}
            {isCompileError && rfneCompileReport && (
              <div className="gd-section" style={{ marginTop: 16 }}>
                <h3 className="gd-section-label">Compile Errors</h3>
                {/* Fix E: note that some errors may be pre-existing in SvcGen output */}
                <div className="rc-refine-todo-note" style={{ marginBottom: 8 }}>
                  <AlertCircle size={13} />
                  <span>
                    Compile errors may be pre-existing in the SvcGen output. Run Pre-Flight to resolve before packaging.
                  </span>
                </div>
                <div className="rc-refine-compile-errors">
                  {(rfneCompileReport.errors || rfneCompileReport.compile_errors || []).slice(0, 8).map((e, i) => (
                    <div key={i} className="rc-refine-compile-error-row">
                      <span className="rc-refine-error-file">{e.file || e.filename || '—'}</span>
                      <span className="rc-refine-error-msg">{e.message || e.error || String(e)}</span>
                    </div>
                  ))}
                  {((rfneCompileReport.errors || rfneCompileReport.compile_errors || []).length > 8) && (
                    <div className="rc-refine-error-overflow">
                      +{(rfneCompileReport.errors || rfneCompileReport.compile_errors).length - 8} more — fix these in the Java editor before re-packaging
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CTA row — sits above the per-file table so all three action buttons
            are visible without scrolling, even when the patch results are long.
            Shown in all states (idle / polling / done / failed). */}
        <div className="rc-plan-launch" style={{ marginTop: 16, paddingBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
          <button
            className="gd-footer-btn secondary"
            onClick={() => setActiveStep(4)}
          >
            Back
          </button>

          <div className="rc-run-refine-wrap">
            {rfneStatus === 'idle' && (
              <button
                className="rc-run-refine-btn"
                onClick={handleStartRefineCode}
              >
                <Play size={15} />
                Run Refine Code
              </button>
            )}

            {rfneStatus === 'starting' && (
              <button className="rc-run-refine-btn disabled" disabled>
                <Loader2 size={15} className="rc-spin-icon" />
                Starting…
              </button>
            )}

            {isPolling && (
              <button className="rc-run-refine-btn disabled" disabled>
                <Loader2 size={15} className="rc-spin-icon" />
                Running…
              </button>
            )}

            {isFailed && (
              <button
                className="rc-run-refine-btn"
                onClick={() => { setRfneStatus('idle'); setRfneError(null) }}
              >
                <RefreshCw size={15} />
                Retry
              </button>
            )}

            {isSucceeded && (
              <button
                className="rc-run-refine-btn secondary"
                onClick={handleDownloadRefinedCode}
                disabled={rfneDownloading}
                title="Download the refined Java source files and patch metadata as a ZIP"
              >
                {rfneDownloading
                  ? <Loader2 size={15} className="rc-spin-icon" />
                  : <Download size={15} />}
                {rfneDownloading ? 'Downloading…' : 'Download Refined Code'}
              </button>
            )}

            {isCompileError && (
              <>
                <button
                  className="rc-run-refine-btn secondary"
                  onClick={handleDownloadRefinedCode}
                  disabled={rfneDownloading}
                  title="Download the refined Java source files and patch metadata as a ZIP"
                >
                  {rfneDownloading
                    ? <Loader2 size={15} className="rc-spin-icon" />
                    : <Download size={15} />}
                  {rfneDownloading ? 'Downloading…' : 'Download Refined Code'}
                </button>
                <span className="rc-run-refine-note" style={{ marginLeft: 10 }}>
                  Compile errors found — fix in Java editor before packaging
                </span>
              </>
            )}
          </div>
        </div>

        {/* Results — Part 2: per-file patch table (below CTA row) — sortable + filterable */}
        {isDone && (
          <>
            {rfnePatchReport?.results && rfnePatchReport.results.length > 0 && (() => {
              // ── Normalise status for display and filtering ─────────────────
              const normaliseStatus = (raw) => {
                const s = (raw || '').toLowerCase()
                if (s === 'todo' || s === 'manual_review') return 'manual_review'
                if (s === 'skipped')                       return 'needs_review'
                if (s === 'annotated')                     return 'annotated'
                if (s === 'applied')                       return 'applied'
                if (s === 'failed')                        return 'failed'
                return s || 'unknown'
              }

              const STATUS_LABEL = {
                applied:        'Applied',
                annotated:      'Annotated',
                manual_review:  'TODO — Manual Review',
                needs_review:   'Needs Review',
                failed:         'Failed',
              }

              // ── Resolve file name — try all known field names ──────────────
              // API uses target_file; kept all fallbacks for other run types.
              const resolveFile = (row) =>
                row.target_file || row.file || row.filename ||
                row.path || row.java_file || row.filepath || '—'

              // ── Collect unique statuses present in the data ────────────────
              const allStatuses = [...new Set(
                rfnePatchReport.results.map(r => normaliseStatus(r.status))
              )].sort()

              // ── Filter ────────────────────────────────────────────────────
              let rows = rfnePatchReport.results.filter(r =>
                patchStatusFilter === '' ||
                normaliseStatus(r.status) === patchStatusFilter
              )

              // ── Sort ──────────────────────────────────────────────────────
              rows = [...rows].sort((a, b) => {
                let va, vb
                if (patchSortCol === 'file') {
                  va = resolveFile(a).toLowerCase()
                  vb = resolveFile(b).toLowerCase()
                } else if (patchSortCol === 'status') {
                  va = normaliseStatus(a.status)
                  vb = normaliseStatus(b.status)
                } else {
                  va = (a.reason || a.note || '').toLowerCase()
                  vb = (b.reason || b.note || '').toLowerCase()
                }
                if (va < vb) return patchSortDir === 'asc' ? -1 : 1
                if (va > vb) return patchSortDir === 'asc' ?  1 : -1
                return 0
              })

              // ── Sort header click handler ──────────────────────────────────
              const handleSort = (col) => {
                if (patchSortCol === col) {
                  setPatchSortDir(d => d === 'asc' ? 'desc' : 'asc')
                } else {
                  setPatchSortCol(col)
                  setPatchSortDir('asc')
                }
              }

              const SortIcon = ({ col }) => {
                if (patchSortCol !== col) return <span className="rc-sort-icon rc-sort-idle">↕</span>
                return <span className="rc-sort-icon rc-sort-active">{patchSortDir === 'asc' ? '↑' : '↓'}</span>
              }

              return (
                <div className="gd-section" style={{ marginTop: 16 }}>
                  <div className="rc-patch-table-toolbar">
                    <h3 className="gd-section-label" style={{ margin: 0 }}>
                      Patch Results by File
                      <span className="gd-section-count">
                        {rows.length}{patchStatusFilter ? ` / ${rfnePatchReport.results.length}` : ''}
                      </span>
                    </h3>

                    {/* Status filter pills */}
                    <div className="rc-patch-filter-pills">
                      <span className="rc-patch-filter-label">Filter:</span>
                      <button
                        className={`rc-patch-pill ${patchStatusFilter === '' ? 'active' : ''}`}
                        onClick={() => setPatchStatusFilter('')}
                      >All</button>
                      {allStatuses.map(st => (
                        <button
                          key={st}
                          className={`rc-patch-pill ${patchStatusFilter === st ? 'active' : ''} ${st}`}
                          onClick={() => setPatchStatusFilter(patchStatusFilter === st ? '' : st)}
                        >
                          {STATUS_LABEL[st] || st}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rc-refine-file-table-wrap">
                    <table className="rc-plan-table rc-refine-file-table">
                      <thead>
                        <tr>
                          <th className="rc-sort-th" onClick={() => handleSort('file')}>
                            File <SortIcon col="file" />
                          </th>
                          <th className="rc-sort-th" onClick={() => handleSort('status')}>
                            Status <SortIcon col="status" />
                          </th>
                          <th className="rc-sort-th" onClick={() => handleSort('notes')}>
                            Notes <SortIcon col="notes" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="rc-patch-no-results">
                              No results match the current filter.
                            </td>
                          </tr>
                        ) : rows.map((row, i) => {
                          const ns = normaliseStatus(row.status)
                          const fileVal = resolveFile(row)
                          return (
                            <tr key={i}>
                              <td className="rc-plan-file" title={fileVal}>{fileVal}</td>
                              <td>
                                {ns === 'applied'       && <span className="rc-refine-row-badge applied">Applied</span>}
                                {ns === 'manual_review' && <span className="rc-refine-row-badge todo">TODO — Manual Review</span>}
                                {ns === 'annotated'     && <span className="rc-refine-row-badge annotated">Annotated</span>}
                                {ns === 'needs_review'  && <span className="rc-refine-row-badge todo">Needs Review</span>}
                                {ns === 'failed'        && <span className="rc-refine-row-badge failed">Failed</span>}
                                {!STATUS_LABEL[ns]      && <span className="rc-refine-row-badge">{row.status || '—'}</span>}
                              </td>
                              <td className="rc-refine-error-msg">{row.reason || row.note || ''}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })()}

            {rfneReportLoading && (
              <div className="rc-refine-report-loading">
                <Loader2 size={13} className="rc-spin-icon" />
                <span>Loading detailed results…</span>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // ── Context panel (steps 1–2 and 4) ──────────────────────────────────────

  const renderContextPanel = () => (
    <aside className="gd-context-panel">
      <p className="gd-context-heading">REFACTOR SUMMARY</p>

      <div className="gd-context-block">
        <span className="gd-context-label">Project</span>
        <span className="gd-context-value">{projectName}</span>
      </div>
      <div className="gd-context-block">
        <span className="gd-context-label">Scope</span>
        <span className="gd-context-value">
          {scopeMode === 'all' ? 'All Services' : `${selectedServices.size} selected`}
        </span>
      </div>
      <div className="gd-context-block">
        <span className="gd-context-label">Patterns Enabled</span>
        <span className="gd-context-value">
          {Object.values(enabledPatterns).filter(Boolean).length} of {PATTERNS.length}
        </span>
      </div>

      {jobId && (
        <div className="gd-context-block">
          <span className="gd-context-label">Job Status</span>
          <span className="gd-context-value">{jobStatus}</span>
        </div>
      )}

      <div className="gd-context-divider" />
      <div className="gd-context-block">
        <span className="gd-context-label">Confirmed</span>
        <span className="gd-context-value on">{confirmedCount}</span>
      </div>
      <div className="gd-context-block">
        <span className="gd-context-label">Skipped</span>
        <span className="gd-context-value off">{skippedCount}</span>
      </div>
      <div className="gd-context-block">
        <span className="gd-context-label">Pending</span>
        <span className="gd-context-value">{pendingCount}</span>
      </div>

      <div className="gd-context-divider" />
      <p className="gd-context-note">
        Recommendations are generated from Code Analysis output and COBOL-to-Java mappings.
        {finalized && ' Decisions finalized and saved to S3.'}
      </p>
    </aside>
  )

  // ── Header ────────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className="runs-header-bar">
      <div className="runs-header-left">
        <button className="runs-back-btn" onClick={onBack}>
          <ArrowLeft size={15} />
          <span>Back</span>
        </button>
        <div className="runs-breadcrumb">
          <span className="runs-bc-project">{projectName}</span>
          <ChevronRight size={14} className="runs-bc-sep" />
          <span className="runs-bc-page">Refactor Code</span>
        </div>
      </div>

      <div className="runs-header-center">
        <div className="mode-switcher">
          <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
            <Home size={16} /><span>Home</span>
          </button>
          <button className="mode-btn" onClick={() => onGoToAnalyze?.()} title="Analyze">
            <BarChart2 size={16} /><span>Analyze</span>
          </button>
          {/* Workflow and MCP hidden — accessible via Journey dropdown
          <button className="mode-btn" disabled title="Workflow — not available here">
            <Workflow size={16} /><span>Workflow</span>
          </button>
          <button className="mode-btn" disabled title="MCP — coming soon">
            <Plug size={16} /><span>MCP</span>
          </button>
          */}
          <button className="mode-btn" onClick={() => onOpenReports?.()} title="Reports">
            <FileBarChart size={16} /><span>Reports</span>
          </button>

          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              title="HITL Journey"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                    <span>Analyze</span>
                  </button>
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToExtractBizLogic?.() }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToDecomposeCode?.() }}>
                    <span>Define Services</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}>
                    <span>Build Services</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                    <span>Refactor Code</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                    <span>Refine Code</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}>
                    <span>Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="runs-header-right">
        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help">
          <HelpCircle size={18} />
        </button>
        <div className="user-menu-container">
          <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
            <div className="user-avatar"><User size={16} /></div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>
          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || ''}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                <Settings size={16} /><span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                <Home size={16} /><span>Back</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ── Footer for wizard steps 1 & 2 (steps 3–5 have own footers) ──────────

  const showFooter = activeStep === 1 || activeStep === 2

  const handleContinue = () => {
    if (activeStep === 1) {
      setActiveStep(2)
    } else if (activeStep === 2) {
      // If analysis already completed, go straight to step 3
      if (jobStatus === 'completed') {
        setActiveStep(3)
      } else {
        // Kick off analysis then advance (poll will auto-advance when done)
        handleStartAnalysis()
      }
    }
  }

  const step2ContinueLabel = () => {
    if (activeStep !== 2) return 'Continue'
    if (jobStatus === 'starting') return 'Starting…'
    if (jobStatus === 'running') return 'Analyzing…'
    if (jobStatus === 'completed') return 'Continue'
    return 'Start Analysis'
  }

  // ── Root render ───────────────────────────────────────────────────────────

  return (
    <div className="rc-view">
      {renderHeader()}

      <div className={`rc-body ${activeStep === 3 ? 'workspace-mode' : ''}`}>

        {/* Left sidebar — step guide (hidden in step 3 workspace) */}
        {activeStep !== 3 && (
          <aside className="gd-sidebar">
            <p className="gd-sidebar-heading">REFACTOR WORKFLOW</p>
            <p className="gd-sidebar-sub">Follow the steps to build your refactor plan</p>
            <div className="gd-step-list">
              {STEPS.map(step => {
                // Step 5 unlocks once finalized
                const unlocked = step.id <= 4 || finalized
                const isDone   = step.id < activeStep
                const isActive = step.id === activeStep
                const isLocked = !unlocked || step.id > activeStep
                return (
                  <button
                    key={step.id}
                    className={`gd-step-btn ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isLocked ? 'locked' : ''}`}
                    onClick={() => !isLocked && setActiveStep(step.id)}
                    disabled={isLocked}
                  >
                    <div className="gd-step-icon">
                      {isDone    ? <CheckCircle2 size={16} />
                      : isLocked ? <Lock size={14} />
                      :            <Circle size={16} />}
                    </div>
                    <div className="gd-step-text">
                      <span className="gd-step-label">{step.label}</span>
                      <span className="gd-step-desc">{step.desc}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </aside>
        )}

        {/* Step 3 mini step indicator (inline, not sidebar) */}
        {activeStep === 3 && (
          <div className="rc-workspace-step-bar">
            {STEPS.map(step => {
              const isDone   = step.id < activeStep
              const isActive = step.id === activeStep
              // Step 5 only navigable if finalized
              const canClick = step.id < activeStep || (step.id === 5 && finalized)
              return (
                <button
                  key={step.id}
                  className={`rc-ws-step-pill ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                  onClick={() => canClick && setActiveStep(step.id)}
                  disabled={!canClick && step.id > activeStep}
                >
                  {isDone ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                  <span>{step.label}</span>
                </button>
              )
            })}
          </div>
        )}

        {/* Main content */}
        {activeStep === 3 ? (
          renderStep3()
        ) : (
          <main className="gd-main">
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
            {activeStep === 4 && renderStep4()}
            {activeStep === 5 && renderStep5()}

            {showFooter && (
              <div className="gd-step-footer">
                {activeStep > 1 && (
                  <button
                    className="gd-footer-btn secondary"
                    onClick={() => setActiveStep(s => s - 1)}
                    disabled={jobStatus === 'starting' || jobStatus === 'running'}
                  >
                    Back
                  </button>
                )}
                <button
                  className="gd-footer-btn primary"
                  onClick={handleContinue}
                  disabled={jobStatus === 'starting' || jobStatus === 'running'}
                >
                  {step2ContinueLabel()}
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </main>
        )}

        {/* Right context panel (not shown in step 3 — it has its own right panel) */}
        {activeStep !== 3 && renderContextPanel()}
      </div>
    </div>
  )
}

export default RefactorCodeView
